import type { DevEnv } from "../../../../types/environment.ts";
import {
  AWS_RUN_SHELL_SCRIPT_DOCUMENT_NAME,
  GET_SSH_HOST_KEYS_DOCUMENT_REFERENCE,
  MANAGED_BY_P0,
  P0_TAG_NAME,
  PROVISION_USER_ACCESS_DOCUMENT_REFERENCE,
} from "../constants.ts";
import type { AwsFederatedLogin } from "../types.ts";
import { type AwsPartition, arnInfo } from "../util.ts";

export const p0GrantsPathSegment = (
  context: DevEnv,
  samlProviderType: AwsFederatedLogin["provider"]["type"]
) =>
  `p0-grants${samlProviderType === "okta" ? "" : "-workspace"}${context.environmentType === "devLike" ? `-dev-${context.developer}` : ""}`;

export type AwsPolicyOptions = {
  sshOnly?: boolean;
  vpcId?: string;
  restrictResourceToARNs?: string[];
  region?: string;
  tagPermissionSets?: boolean;
};

export const rdsLambdaName = (vpcId: string) =>
  `p0-rds-security-perimeter-${vpcId}`;

export const rdsLambdaArn = (
  region: string,
  account: string,
  vpcId: string,
  awsPartition: AwsPartition
) =>
  `arn:${awsPartition}:lambda:${region}:${account}:function:${rdsLambdaName(vpcId)}`;

export const iamAssessorPolicy = () => ({
  Version: "2012-10-17",
  Statement: [
    {
      Sid: "P0CanReadPoliciesAndListResources",
      Effect: "Allow",
      Action: [
        "access-analyzer:GetFinding",
        "access-analyzer:ListFindings",
        "account:ListRegions",
        "ce:GetDimensionValues",
        "iam:GetAccessKeyLastUsed",
        "iam:GetAccountSummary",
        "iam:GetGroup",
        "iam:GetGroupPolicy",
        "iam:GetLoginProfile",
        "iam:GetPolicy",
        "iam:GetPolicyVersion",
        "iam:GetRole",
        "iam:GetRolePolicy",
        "iam:GetSAMLProvider",
        "iam:GetUser",
        "iam:GetUserPolicy",
        "iam:ListAccessKeys",
        "iam:ListAccountAliases",
        "iam:ListAttachedGroupPolicies",
        "iam:ListAttachedRolePolicies",
        "iam:ListAttachedUserPolicies",
        "iam:ListGroupPolicies",
        "iam:ListGroups",
        "iam:ListGroupsForUser",
        "iam:ListMFADevices",
        "iam:ListPolicies",
        "iam:ListPolicyTags",
        "iam:ListPolicyVersions",
        "iam:ListRolePolicies",
        "iam:ListRoles",
        "iam:ListUserPolicies",
        "iam:ListUsers",
        "iam:ListUserTags",
        "identitystore:ListGroups",
        "identitystore:ListGroupMemberships",
        "identitystore:ListUsers",
        "resource-explorer-2:ListIndexes",
        "resource-explorer-2:Search",
        "sso:DescribePermissionSet",
        "sso:ListAccountAssignments",
        "sso:ListInstances",
        "sso:ListPermissionSetsProvisionedToAccount",
      ],
      Resource: "*",
    },
  ],
});

export const rdsSecurityPerimeterPolicy = ({
  awsAccountId,
  awsPartition,
  options: { vpcId, region },
}: {
  awsAccountId: string;
  awsPartition: AwsPartition;
  options: AwsPolicyOptions;
}) => {
  if (!vpcId || !region) {
    throw new Error(
      "vpcId and region must be provided for RDS security perimeter policy"
    );
  }
  return {
    Version: "2012-10-17",
    Statement: [
      {
        Sid: "P0CanInvokeRdsSecurityPerimeter",
        Effect: "Allow",
        Action: ["lambda:InvokeFunction"],
        Resource: rdsLambdaArn(region, awsAccountId, vpcId, awsPartition),
        Condition: {
          StringEquals: {
            "aws:ResourceAccount": awsAccountId,
          },
        },
      },
    ],
  };
};

/** Identity policy for the P0ResourceInventory role */
export const resourceInventoryPolicy = ({
  awsAccountId,
}: {
  awsAccountId: string;
}) => ({
  Version: "2012-10-17",
  Statement: [
    {
      Sid: "P0CanListResources",
      Effect: "Allow",
      // GetRole and GetRolePolicy are for install verification
      Action: [
        "resource-explorer-2:ListIndexes",
        "resource-explorer-2:Search",
        "iam:GetRole",
        "iam:GetRolePolicy",
      ],
      Resource: "*",
      Condition: {
        StringEquals: {
          "aws:ResourceAccount": awsAccountId,
        },
      },
    },
  ],
});

export const roleReaderPolicy = ({
  awsPartition,
  roleName,
  awsAccountId,
}: {
  awsPartition: AwsPartition;
  roleName: string;
  awsAccountId: string;
}) => ({
  Version: "2012-10-17",
  Statement: [
    {
      Sid: "P0CanListAccountAliases",
      Effect: "Allow",
      Action: ["iam:ListAccountAliases"],
      Resource: "*",
      Condition: {
        StringEquals: {
          "aws:ResourceAccount": awsAccountId,
        },
      },
    },
    {
      Sid: "P0CanReadOwnPolicyForValidation",
      Effect: "Allow",
      Action: ["iam:GetRole", "iam:ListRoles", "iam:GetRolePolicy"],
      Resource: [`arn:${awsPartition}:iam::${awsAccountId}:role/${roleName}`],
      Condition: {
        StringEquals: {
          "aws:ResourceAccount": awsAccountId,
        },
      },
    },
  ],
});

export const accessLogsPolicy = ({
  awsAccountId,
  options: { restrictResourceToARNs },
}: {
  awsAccountId: string;
  options: {
    restrictResourceToARNs?: string[];
  };
}) => ({
  Version: "2012-10-17",
  Statement: [
    {
      Sid: "P0CanReadCloudTrailLogs",
      Effect: "Allow",
      Action: ["cloudtrail:GetQueryResults", "cloudtrail:StartQuery"],
      Resource: restrictResourceToARNs?.length ? restrictResourceToARNs : "*",
      Condition: {
        StringEquals: {
          "aws:ResourceAccount": awsAccountId,
        },
      },
    },
    {
      Sid: "P0CanReadCloudTrailLogsIAM",
      Effect: "Allow",
      Action: ["iam:GetRole", "iam:GetRolePolicy"],
      Resource: "*",
      Condition: {
        StringEquals: {
          "aws:ResourceAccount": awsAccountId,
        },
      },
    },
  ],
});

const sanitizeSid = (sid: string) => {
  // Remove any characters that are not alphanumeric or underscore
  // This is to prevent any issues with IAM policy validation
  // see: https://docs.aws.amazon.com/IAM/latest/UserGuide/reference_policies_elements_sid.html
  return sid.replace(/[^a-zA-Z0-9_]/g, "");
};

export const functionCallerPolicyName = (name: string) => `P0Invoke${name}`;

export const functionCallerPolicy = ({
  lambdaArn,
}: {
  lambdaArn: string;
}): object => {
  const info = arnInfo(lambdaArn);
  const awsAccountId = info.accountId ?? "";

  if (!info.name) {
    throw new Error("Lambda name could not be determined from ARN");
  }

  return {
    Version: "2012-10-17",
    Statement: [
      {
        Sid: `P0CanInvoke${sanitizeSid(info.name)}`,
        Effect: "Allow",
        Action: ["lambda:InvokeFunction"],
        Resource: [lambdaArn],
        Condition: {
          StringEquals: {
            "aws:ResourceAccount": awsAccountId,
          },
        },
      },
    ],
  };
};

/** Identity policy for the least-privilege P0IamManager role used only for SSH access.
 *  This was requested by Splunk, due to security concerns (they only use SSH currently).
 */
const sshIamManagerPolicy = ({
  context,
  awsAccountId,
  awsPartition,
  roleName,
}: {
  context: DevEnv;
  awsAccountId: string;
  awsPartition: AwsPartition;
  roleName: string;
}) => {
  const pathPrefix = p0GrantsPathSegment(context, "okta");

  return {
    Version: "2012-10-17",
    Statement: [
      {
        Sid: "P0CanReadItsOwnRoleForValidation",
        Effect: "Allow",
        Action: ["iam:GetRole", "iam:GetRolePolicy"],
        Resource: `arn:${awsPartition}:iam::${awsAccountId}:role/${roleName}`,
      },
      {
        Sid: "P0CanReadSsmDocumentsForSSH",
        Effect: "Allow",
        Action: "ssm:GetDocument",
        Resource: [
          `arn:${awsPartition}:ssm:*:${awsAccountId}:document/${PROVISION_USER_ACCESS_DOCUMENT_REFERENCE.documentName}`,
          `arn:${awsPartition}:ssm:*:${awsAccountId}:document/${GET_SSH_HOST_KEYS_DOCUMENT_REFERENCE.documentName}`,
        ],
      },
      {
        Sid: "P0CanReadAccountInformation",
        Effect: "Allow",
        Action: [
          "account:ListRegions",
          "iam:ListAccountAliases",
          "iam:GetSAMLProvider",
        ],
        Resource: "*",
        Condition: {
          StringEquals: {
            "aws:ResourceAccount": awsAccountId,
          },
        },
      },
      {
        Sid: "P0CanCreateP0ManagedPolicies",
        Effect: "Allow",
        Action: ["iam:CreatePolicy", "iam:TagPolicy"],
        Resource: `arn:${awsPartition}:iam::${awsAccountId}:policy/P0Policy*`,
        Condition: {
          StringEquals: {
            [`aws:RequestTag/${P0_TAG_NAME}`]: MANAGED_BY_P0,
          },
        },
      },
      {
        Sid: "P0CanChangeP0ManagedPolicies",
        Effect: "Allow",
        Action: [
          "iam:CreatePolicyVersion",
          "iam:DeletePolicy",
          "iam:DeletePolicyVersion",
          "iam:GetPolicy",
          "iam:GetPolicyVersion",
          "iam:ListPolicyVersions",
        ],
        Resource: `arn:${awsPartition}:iam::${awsAccountId}:policy/P0Policy*`,
      },
      {
        Sid: "P0CanListP0ManagedRoles",
        Effect: "Allow",
        Action: "iam:ListRoles",
        Resource: `arn:${awsPartition}:iam::${awsAccountId}:role/${pathPrefix}/*`,
      },
      {
        Sid: "P0CanChangeP0ManagedRoles",
        Effect: "Allow",
        Action: [
          "iam:DeleteRolePolicy",
          "iam:GetRole",
          "iam:GetRolePolicy",
          "iam:ListAttachedRolePolicies",
          "iam:ListRolePolicies",
          "iam:ListRoles",
          "iam:PutRolePolicy",
        ],
        Resource: `arn:${awsPartition}:iam::${awsAccountId}:role/${pathPrefix}/P0GrantsRole*`,
      },
      {
        Sid: "P0CanAttachP0ManagedPoliciesToP0ManagedRoles",
        Effect: "Allow",
        Action: ["iam:AttachRolePolicy", "iam:DetachRolePolicy"],
        Resource: `arn:${awsPartition}:iam::${awsAccountId}:role/${pathPrefix}/P0GrantsRole*`,
        Condition: {
          StringLike: {
            "iam:PolicyARN": `arn:${awsPartition}:iam::${awsAccountId}:policy/P0Policy*`,
          },
        },
      },
      {
        Sid: "P0CanManageSshAccess",
        Effect: "Allow",
        Action: [
          "ec2:DescribeInstanceStatus",
          "ec2:DescribeInstances",
          "ec2:DescribeTags",
          "ssm:DescribeInstanceInformation",
          "ssm:DescribeSessions",
          "ssm:GetCommandInvocation",
          "ssm:TerminateSession",
        ],
        Resource: "*",
        Condition: {
          StringEquals: {
            "aws:ResourceAccount": awsAccountId,
          },
        },
      },
      {
        Sid: "P0CanUseSsmDocsForSshAccess",
        Effect: "Allow",
        Action: "ssm:SendCommand",
        Resource: [
          `arn:${awsPartition}:ec2:*:${awsAccountId}:instance/*`,
          `arn:${awsPartition}:ssm:*:${awsAccountId}:document/${PROVISION_USER_ACCESS_DOCUMENT_REFERENCE.documentName}`,
          `arn:${awsPartition}:ssm:*:${awsAccountId}:document/${GET_SSH_HOST_KEYS_DOCUMENT_REFERENCE.documentName}`,
          `arn:${awsPartition}:ssm:*::document/${AWS_RUN_SHELL_SCRIPT_DOCUMENT_NAME}`,
        ],
      },
      {
        Sid: "P0CanNotAlterP0Roles",
        Effect: "Deny",
        Action: [
          "iam:AttachRole*",
          "iam:DeleteRole*",
          "iam:DetachRole*",
          "iam:PutRole*",
          "iam:TagRole",
          "iam:UntagRole",
          "iam:UpdateRole*",
        ],
        Resource: `arn:${awsPartition}:iam::${awsAccountId}:role/P0Role*`,
      },
      {
        Sid: "P0CannotModifySSMDocuments",
        Effect: "Deny",
        Action: [
          "ssm:CreateDocument",
          "ssm:UpdateDocument",
          "ssm:DeleteDocument",
        ],
        Resource: "*",
      },
      {
        Sid: "P0CanNotAssumeRoles",
        Effect: "Deny",
        Action: "sts:AssumeRole",
        Resource: "*",
      },
    ],
  };
};

/** Identity policy for the P0IamManager role */
const fullIamManagerPolicy = ({
  awsAccountId,
  awsPartition,
  options,
}: {
  awsAccountId: string;
  awsPartition: AwsPartition;
  roleName: string;
  options: AwsPolicyOptions;
}) => {
  return {
    Version: "2012-10-17",
    Statement: [
      {
        Sid: "P0CanGetAndListPolicies",
        Effect: "Allow",
        Action: [
          "iam:GetPolicy",
          "iam:GetPolicyVersion",
          "iam:ListPolicyTags",
          "iam:ListPolicyVersions",
        ],
        Resource: "*",
      },
      {
        Sid: "P0CanManagePoliciesAndListResources",
        Effect: "Allow",
        Action: [
          "account:ListRegions",
          "iam:AddUserToGroup",
          "iam:AttachRolePolicy",
          "iam:AttachUserPolicy",
          "iam:CreatePolicy",
          "iam:CreatePolicyVersion",
          "iam:DeletePolicy",
          "iam:DeletePolicyVersion",
          "iam:DeleteRole",
          "iam:DeleteRolePolicy",
          "iam:DetachRolePolicy",
          "iam:DetachUserPolicy",
          "iam:GetRole",
          "iam:GetRolePolicy",
          "iam:GetSAMLProvider",
          "iam:GetUser",
          "iam:ListAccountAliases",
          "iam:ListAttachedGroupPolicies",
          "iam:ListAttachedRolePolicies",
          "iam:ListAttachedUserPolicies",
          "iam:ListGroupPolicies",
          "iam:ListGroups",
          "iam:ListGroupsForUser",
          "iam:ListPolicies",
          "iam:ListRolePolicies",
          "iam:ListRoles",
          "iam:ListUsers",
          "iam:ListUserTags",
          "iam:PutRolePolicy",
          "iam:RemoveUserFromGroup",
          "ec2:DescribeInstances",
          "resource-explorer-2:ListIndexes",
          "resource-explorer-2:Search",
          "sagemaker:ListNotebookInstances",
        ],
        Resource: "*",
        Condition: {
          StringEquals: {
            "aws:ResourceAccount": awsAccountId,
          },
        },
      },
      {
        Sid: "P0CanManageSshAccess",
        Effect: "Allow",
        Action: [
          "ec2:DescribeInstanceStatus",
          "ec2:DescribeInstances",
          "ec2:DescribeTags",
          "ssm:AddTagsToResource",
          "ssm:GetDocument",
          "ssm:DescribeInstanceInformation",
          "ssm:DescribeSessions",
          "ssm:GetCommandInvocation",
          "ssm:ListCommandInvocations",
          "ssm:TerminateSession",
        ],
        Resource: "*",
        Condition: {
          StringEquals: {
            "aws:ResourceAccount": awsAccountId,
          },
        },
      },
      {
        Sid: "P0CanUseSsmDocsForSshAccess",
        Effect: "Allow",
        Action: "ssm:SendCommand",
        Resource: [
          `arn:${awsPartition}:ec2:*:${awsAccountId}:instance/*`,
          `arn:${awsPartition}:ssm:*:${awsAccountId}:document/${PROVISION_USER_ACCESS_DOCUMENT_REFERENCE.documentName}`,
          `arn:${awsPartition}:ssm:*:${awsAccountId}:document/${GET_SSH_HOST_KEYS_DOCUMENT_REFERENCE.documentName}`,
          `arn:${awsPartition}:ssm:*::document/${AWS_RUN_SHELL_SCRIPT_DOCUMENT_NAME}`,
        ],
      },
      {
        Sid: "P0CanManageKubernetesAccess",
        Effect: "Allow",
        Action: [
          "eks:CreateAccessEntry",
          "eks:DeleteAccessEntry",
          "eks:DescribeAccessEntry",
          "eks:UpdateAccessEntry",
        ],
        Resource: "*",
        Condition: {
          StringEquals: {
            "aws:ResourceAccount": awsAccountId,
          },
          ArnNotLike: {
            "eks:principalArn": `arn:${awsPartition}:iam::${awsAccountId}:role/P0Role*`,
          },
        },
      },
      {
        Sid: "P0CanManageSsoAssignments",
        Effect: "Allow",
        Action: [
          "iam:GetSAMLProvider",
          "identitystore:ListUsers",
          "sso:AttachCustomerManagedPolicyReferenceToPermissionSet",
          "sso:AttachManagedPolicyToPermissionSet",
          "sso:CreateAccountAssignment",
          "sso:CreatePermissionSet",
          "sso:DeleteAccountAssignment",
          "sso:DeletePermissionSet",
          "sso:DescribeAccountAssignmentCreationStatus",
          "sso:DescribeAccountAssignmentDeletionStatus",
          "sso:DescribePermissionSet",
          "sso:DescribePermissionSetProvisioningStatus",
          "sso:GetInlinePolicyForPermissionSet",
          "sso:ListAccountAssignments",
          "sso:ListInstances",
          "sso:ListManagedPoliciesInPermissionSet",
          "sso:ListCustomerManagedPolicyReferencesInPermissionSet",
          "sso:ListPermissionSets",
          "sso:ListTagsForResource",
          "sso:ProvisionPermissionSet",
          "sso:PutInlinePolicyToPermissionSet",
          ...(options.tagPermissionSets ? ["sso:TagResource"] : []),
        ],
        Resource: "*",
        Condition: {
          StringEquals: {
            "aws:ResourceAccount": awsAccountId,
          },
        },
      },
      {
        Sid: "P0CanCreateSsoRolesOnly",
        Effect: "Allow",
        Action: "iam:CreateRole",
        // Granting on all resources would allow an attacker that compromised P0 to
        // privilege escalate: they could create a role with an arbitrary trust boundary.
        // Prevent this by restricting created roles to only those generated via AWS SSO;
        // such roles are always restricted to a valid SSO identity. Note that forward
        // slashes are not allowed in role names, so this ARN can not be impersonated by
        // manually including this previx in the role name.
        Resource: `arn:${awsPartition}:iam::${awsAccountId}:role/aws-reserved/sso.amazonaws.com/*`,
      },
      {
        Sid: "P0CanTagPoliciesAndRoles",
        Effect: "Allow",
        Action: ["iam:CreatePolicy", "iam:TagPolicy"],
        Resource: "*",
        Condition: {
          StringEquals: {
            [`aws:RequestTag/${P0_TAG_NAME}`]: MANAGED_BY_P0,
            "aws:ResourceAccount": awsAccountId,
          },
        },
      },
      {
        Sid: "P0CanNotAlterItsOwnRole",
        Effect: "Deny",
        Action: [
          "iam:AttachRole*",
          "iam:DeleteRole*",
          "iam:DetachRole*",
          "iam:PutRole*",
          "iam:TagRole",
          "iam:UntagRole",
          "iam:UpdateRole*",
        ],
        Resource: `arn:${awsPartition}:iam::${awsAccountId}:role/P0Role*`,
      },
      {
        Sid: "P0CannotAlterSsmDocuments",
        Effect: "Deny",
        Action: [
          "ssm:CreateDocument",
          "ssm:DeleteDocument",
          "ssm:UpdateDocument",
        ],
        Resource: [
          `arn:${awsPartition}:ssm:*:${awsAccountId}:document/${PROVISION_USER_ACCESS_DOCUMENT_REFERENCE.documentName}`,
          `arn:${awsPartition}:ssm:*:${awsAccountId}:document/${GET_SSH_HOST_KEYS_DOCUMENT_REFERENCE.documentName}`,
        ],
      },
      {
        Sid: "P0CanNotAssumeRoles",
        Effect: "Deny",
        Action: "sts:AssumeRole",
        Resource: "*",
      },
      ...(awsPartition === "aws-us-gov"
        ? [
            {
              Sid: "P0CanListBuckets",
              Effect: "Allow",
              Action: ["s3:ListAllMyBuckets"],
              Resource: "*",
              Condition: {
                StringEquals: {
                  "aws:ResourceAccount": awsAccountId,
                },
              },
            },
          ]
        : []),
    ],
  };
};

export const iamManagerPolicy = (params: {
  context: DevEnv;
  awsAccountId: string;
  awsPartition: AwsPartition;
  roleName: string;
  options: AwsPolicyOptions;
}) =>
  params.options.sshOnly
    ? sshIamManagerPolicy(params)
    : fullIamManagerPolicy(params);
